/* eslint-disable react/style-prop-object */
import React, { ReactElement, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { delay } from "helpers/app";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import useMediaQuery from "@mui/material/useMediaQuery";

import { dbSteps } from "constants/cluster";
import { fqdnRegex } from "constants/regexp";

import LoadingComponent from "components/shared/loaders/LoadingComponent";
import AppModal from "components/shared/modal/AppModal";
import UserWaiting from "components/shared/progressBar/UserWaiting";

import { postCreateClusterDatabase } from "redux/handlers/clusterHandler";
import { postCheckServerName, postServerDetails } from "redux/handlers/serverHandler";

import { getServerDetails } from "redux/selectors/serversSelector";
import { getUserBalance } from "redux/selectors/userSelector";

import { useAppDispatch } from "hooks/reduxHook";

import ClusterRecap from "./clusterWizardComponent/ClusterRecap";

const ClusterDbWizard = (): ReactElement => {
 const intl = useIntl();
 const navigate = useNavigate();
 const dispatch = useAppDispatch();
 const params = useParams<{ id: string; section: string }>();
 const theme = useTheme();
 const desktopViewPort = useMediaQuery(theme.breakpoints.up("lg"));

 const server = useSelector(getServerDetails);
 const userBalance = useSelector(getUserBalance);

 const [activeStep, setActiveStep] = useState<number>(0);
 const [openModal, setOpenModal] = useState<boolean>(false);
 const [isLoading, setIsLoading] = useState<boolean>(true);
 const [loading, setLoading] = useState<boolean>(false);
 const [nameChecked, setNameChecked] = useState<string>("");
 const [stepLoading, setStepLoading] = useState<boolean>(false);

 useEffect(() => {
  (async () => {
   setIsLoading(true);
   await dispatch(postServerDetails(Number(params.id)));
   setIsLoading(false);
  })();
 }, []);

 const checkServerName = async () => {
  setStepLoading(true);
  setNameChecked(await dispatch(postCheckServerName(watch("server_name"))));
  setStepLoading(false);
 };

 const handleOpen = () => setOpenModal(true);
 const handleClose = () => setOpenModal(false);
 const handleReset = () => {
  reset();
  setActiveStep(0);
 };

 const handleNext = () => {
  setActiveStep((prevActiveStep) => prevActiveStep + 1);
 };

 const handleBack = () => {
  setActiveStep((prevActiveStep) => prevActiveStep - 1);
 };

 const { control, formState, reset, watch } = useForm({
  defaultValues: {
   server_name: ""
  }
 });

 const handleConfirm = async () => {
  setLoading(true);
  await dispatch(postCreateClusterDatabase(server.id || 0, watch("server_name"), server.price));
  await delay(3000);
  setLoading(false);
  handleClose();
  navigate(`/servers/cluster/${params.id}`);
  handleReset();
 };

 const renderStepContent = (index: number): ReactElement => {
  switch (index) {
   case 0:
    return (
     <Controller
      name="server_name"
      control={control}
      rules={{
       required: true
      }}
      render={({ field }) => (
       <TextField
        {...field}
        fullWidth={true}
        value={watch("server_name")}
        label={intl.formatMessage({ id: "cluster.add.serverName" })}
        error={formState.isDirty && !!formState?.errors?.server_name}
        InputLabelProps={{ shrink: true }}
        sx={{ my: 2 }}
        autoComplete="off"
        helperText={
         formState.isDirty &&
         !!formState?.errors?.server_name &&
         intl.formatMessage({ id: "login.passwordMinLength" })
        }
       />
      )}
     />
    );
   default:
    return <></>;
  }
 };

 const renderServerChecked = () => {
  switch (nameChecked) {
   case "name available":
    return (
     <Alert severity="success">
      <FormattedMessage id="server.add.nameAvailable" />
     </Alert>
    );
   default:
    return (
     <Alert severity="error">
      <FormattedMessage id="server.add.nameNotAvailable" />
     </Alert>
    );
  }
 };

 const disableNextButton = (index: number) => {
  switch (index) {
   case 0:
    return watch("server_name") === "";
   default:
    return false;
  }
 };

 const checkDisableConfirm = () => {
  return watch("server_name") === "";
 };

 return !isLoading ? (
  <Container maxWidth={false} sx={{ mx: 2, width: "auto" }}>
   <Grid container spacing={3} sx={{ mt: 3 }}>
    <Grid xs={desktopViewPort ? 3 : 12} sx={{ textAlign: "left" }}>
     <Typography variant="unboundedTitle" fontSize={18} fontWeight="bold">
      <FormattedMessage id={"cluster.add.createDb"} />
     </Typography>
     {desktopViewPort && (
      <Stepper activeStep={activeStep} orientation="vertical">
       {dbSteps.map((step, index) => (
        <Step key={step.label}>
         <StepLabel>
          <FormattedMessage id={step.label} />
         </StepLabel>
        </Step>
       ))}
      </Stepper>
     )}
    </Grid>
    <Grid xs={desktopViewPort ? 6 : 12}>
     <Paper elevation={1} sx={{ borderRadius: "5px", p: 2, textAlign: "left" }}>
      <Typography variant="unboundedTitle" fontSize={18}>
       <FormattedMessage id={dbSteps[activeStep].label} />
      </Typography>
      {!stepLoading ? (
       <Stack mt={2}>
        <Stack spacing={2}>
         <Typography variant="subtitle2">
          <FormattedMessage id={dbSteps[activeStep].description} />
         </Typography>
         {renderStepContent(activeStep)}
         {activeStep === 0 && nameChecked !== "" && renderServerChecked()}
        </Stack>
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
         <Button disabled={activeStep === 0} onClick={handleBack} sx={{ mt: 1, mr: 1 }}>
          <FormattedMessage id="app.wizard.back" />
         </Button>
         {activeStep !== dbSteps.length - 1 ? (
          <Button
           disabled={
            disableNextButton(activeStep) ||
            watch("server_name").length === 0 ||
            !fqdnRegex.test(watch("server_name")) ||
            !watch("server_name").includes(".") ||
            nameChecked === "name not available"
           }
           onClick={() =>
            activeStep === 0 && nameChecked === "" ? checkServerName() : handleNext()
           }
           sx={{ mt: 1, mr: 1 }}>
           <FormattedMessage
            id={
             activeStep === 0 && nameChecked === ""
              ? "server.add.serverCheck"
              : "app.wizard.continue"
            }
           />
          </Button>
         ) : (
          <></>
         )}
         {activeStep === 0 && (
          <>
           <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
            <FormattedMessage id="app.wizard.reset" />
           </Button>
           <Button
            sx={{ mt: 1, mr: 1 }}
            disabled={checkDisableConfirm()}
            onClick={() =>
             activeStep === 0 && nameChecked === "" ? checkServerName() : handleOpen()
            }>
            <FormattedMessage
             id={
              activeStep === 0 && nameChecked === ""
               ? "server.add.serverCheck"
               : "app.wizard.confirm"
             }
            />
           </Button>
          </>
         )}
        </Stack>
       </Stack>
      ) : (
       <Stack mt={2}>
        <Stack spacing={2}>
         <Typography variant="subtitle2">
          <FormattedMessage id="server.checkExecution" />
         </Typography>
         <CircularProgress />
        </Stack>
       </Stack>
      )}
     </Paper>
    </Grid>
    <Grid xs={desktopViewPort ? 3 : 12}>
     <ClusterRecap server_name={watch("server_name")} type="db" />
    </Grid>
   </Grid>
   <AppModal
    open={openModal}
    close={handleClose}
    title={intl.formatMessage({ id: "cluster.add.createNewDb" })}
    handleClose={handleClose}
    disabled={loading}
    handleConfirm={handleConfirm}>
    <Stack>
     <FormattedMessage id="cluster.add.confirmNewDb" />
     <Stack direction="row" spacing={2}>
      <Typography minWidth={150} textAlign="start" fontWeight="bold">
       <FormattedMessage id="server.add.balance" />
      </Typography>
      <Typography>
       <FormattedNumber value={userBalance} style={"currency"} currency="EUR" />
      </Typography>
     </Stack>
     <Stack direction="row" spacing={2} alignItems="center">
      <Typography minWidth={150} textAlign="start" fontWeight="bold">
       <FormattedMessage id="server.add.totalPrice" />
      </Typography>
      <Typography>
       <FormattedNumber value={server.price} style={"currency"} currency="EUR" />
      </Typography>
     </Stack>
    </Stack>
   </AppModal>
   <UserWaiting openLoader={loading} />
  </Container>
 ) : (
  <Container maxWidth={false} sx={{ mx: 2, width: "auto" }}>
   <LoadingComponent section="cluster" />
  </Container>
 );
};

export default ClusterDbWizard;
